// Tested in ../index.test.tsx

import SessionProgressBar from '@/components/session-progress-bar';
import { DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { MaxColumnsContainer } from '@rhg/components/ui/layout';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProgramWorkflowActions } from '@rhg/hooks/use-program-workflow-actions';
import { useProvidedProgramContext } from '@rhg/hooks/use-provided-program-context';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { UserBlocker } from '@rhg/hooks/use-user-blocker/types';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import {
    Box,
    Button,
    ButtonProps,
    Divider,
    Grid,
    Tab,
    Tabs,
    Theme,
} from '@rhg/libs/material';
import { ProgramState } from '@rhg/utils/programs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AccessCodeBanner } from '../../access-code-banner';
import { BANNER_HEIGHT, BANNER_WIDTH } from '../banner-image/types';
import { GetStartedDialog } from '../get-started-dialog';
import { ProgramOverview } from '../overview';
import { ProgramHeader } from '../program-header';
import { ProgramSchedule } from '../schedule';
import { ProgramTab } from './types';

const styleContainer = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
};
const styleContentContainer = {
    flex: 1,
    width: '100%',
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
    display: 'flex',
    justifyContent: 'center',
    pt: DS_SPACING_UNITS.SECTION,
    px: {
        xs: DS_SPACING_UNITS.SIDE_SMALL,
        md: DS_SPACING_UNITS.SIDE_MEDIUM,
        lg: DS_SPACING_UNITS.SIDE_LARGE,
    },
};
const styleContent = {
    maxWidth: '676px',
    width: '100%',
    mb: DS_SPACING_UNITS.SECTION,
};
const styleBannerContainer = {
    width: BANNER_WIDTH,
    height: { xs: 'auto', sm: 'auto', md: BANNER_HEIGHT },
    backgroundColor: (theme: Theme) => theme.palette.background.primary,
};

const styleDivider = { width: '100%', mb: DS_SPACING_UNITS.CONTENT };
const styleAccessCodeBanner = {
    mb: DS_SPACING_UNITS.CONTENT,
};
const styleActions = {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    justifyContent: 'center',
    py: 2,
    px: {
        xs: DS_SPACING_UNITS.SIDE_SMALL,
        md: DS_SPACING_UNITS.SIDE_MEDIUM,
        lg: DS_SPACING_UNITS.SIDE_LARGE,
    },
    alignItems: 'center',
    display: 'flex',
    zIndex: 2,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'background.paper'
            : 'background.primary',
    borderTop: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? `1px solid ${theme.palette.divider}`
            : 'none',
};
const styleAction = { width: '100%' };
const styleProgress = {
    mb: DS_SPACING_UNITS.CONTENT,
};

interface ContextAwareProgramProps {
    showOverview?: boolean;
}

export const ContextAwareProgram = ({
    showOverview,
}: ContextAwareProgramProps) => {
    const {
        numSessionsCompleted,
        programType,
        programState,
        numSessions,
        program,
        workflow,
        currentWorkflowStep,
        packClass,
    } = useProvidedProgramContext();

    const { formatMessage } = useIntl();
    const { userBlocker } = useUserBlocker();
    const {
        menuAnchor,
        getStartedDialogState,
        showHowToGetStartedDialog,
        openMenu,
        closeMenu,
        handleGetStartedClosed,
        onLeaveProgram,
        onJoinProgram,
        onRestartProgram,
    } = useProgramWorkflowActions({
        program,
        workflow,
        currentWorkflowStep,
        packClass,
    });
    const scrollingContainerRef = useRef<HTMLElement>(null);
    const [selectedTab, setSelectedTab] = useState<ProgramTab>(
        programType === 'clinical_program' &&
            (![ProgramState.InProgress, ProgramState.Completed].includes(
                programState
            ) ||
                showOverview)
            ? 'overview'
            : 'schedule'
    );

    const isUnsponsored = useMemo(
        () => userBlocker === UserBlocker.Unsponsored,
        [userBlocker]
    );

    const handleJoinProgram = useCallback(
        (willResetProgress: boolean) => {
            onJoinProgram({
                onSuccess: () => setSelectedTab('schedule'),
                willResetProgress,
            });
        },
        [onJoinProgram]
    );

    const handleLeaveProgram = useCallback(() => {
        onLeaveProgram({ onSuccess: () => setSelectedTab('overview') });
    }, [onLeaveProgram]);

    const handleRestartProgram = useCallback(
        () => onRestartProgram(),
        [onRestartProgram]
    );

    const isSmOrXs = useIsMobile();

    const ctaButtonProps: Pick<ButtonProps, 'trackButton' | 'onClick'> =
        useMemo(() => {
            if (programState === ProgramState.InProgress) {
                return {} as ButtonProps;
            }
            if (programState === ProgramState.Completed) {
                return {
                    trackButton: 'CtaRestartProgram',
                    onClick: handleRestartProgram,
                };
            }
            return {
                trackButton: 'CtaJoinProgram',
                onClick: () => handleJoinProgram(false),
            };
        }, [handleJoinProgram, handleRestartProgram, programState]);

    return (
        <Box ref={scrollingContainerRef} sx={styleContainer}>
            <Box sx={styleBannerContainer}>
                <ProgramHeader
                    onJoinProgramClicked={() => handleJoinProgram(false)}
                    onLeaveProgramClicked={handleLeaveProgram}
                    onShowHowToGetStartedDialogClicked={() =>
                        showHowToGetStartedDialog(false)
                    }
                    closeMoreMenu={closeMenu}
                    moreAnchor={menuAnchor}
                    openMoreMenu={openMenu}
                    onRestartProgramClicked={handleRestartProgram}
                />
            </Box>
            <Box sx={styleContentContainer}>
                <Box sx={styleContent}>
                    {programType === 'clinical_program' && (
                        <Tabs
                            value={selectedTab}
                            onChange={(_, newTab) => setSelectedTab(newTab)}
                            textColor="secondary"
                            indicatorColor="secondary"
                            centered
                        >
                            <Tab
                                trackButton="ProgramScheduleTab"
                                value="schedule"
                                label={formatMessage(
                                    commonMessages['common.schedule']
                                )}
                                tabIndex={0}
                            />
                            <Tab
                                trackButton="ProgramOverviewTab"
                                value="overview"
                                label={formatMessage(
                                    commonMessages['common.overview']
                                )}
                                tabIndex={0}
                            />
                        </Tabs>
                    )}
                    <Divider
                        orientation="horizontal"
                        sx={styleDivider}
                        aria-hidden
                    />
                    {isUnsponsored && (
                        <Grid item xs={12}>
                            <AccessCodeBanner sx={styleAccessCodeBanner} />
                            <Divider
                                aria-hidden
                                orientation="horizontal"
                                sx={styleDivider}
                            />
                        </Grid>
                    )}
                    {isSmOrXs &&
                        programState === ProgramState.InProgress &&
                        numSessions && (
                            <SessionProgressBar
                                currentStep={numSessionsCompleted}
                                totalSteps={numSessions}
                                sx={styleProgress}
                                isClincialProgram={
                                    programType === 'clinical_program'
                                }
                            />
                        )}
                    {selectedTab === 'overview' && <ProgramOverview />}
                    {selectedTab === 'schedule' && (
                        <ProgramSchedule
                            onJoin={() => handleJoinProgram(false)}
                        />
                    )}
                </Box>
            </Box>
            {programType === 'clinical_program' &&
                programState !== ProgramState.InProgress && (
                    <Box sx={styleActions}>
                        <MaxColumnsContainer columns={6}>
                            <Button
                                size="large"
                                variant="contained"
                                color="primary"
                                {...ctaButtonProps}
                                sx={styleAction}
                            >
                                {programState === ProgramState.Completed
                                    ? formatMessage(
                                          programMessages[
                                              'program.restart_program'
                                          ]
                                      )
                                    : formatMessage(
                                          programMessages[
                                              'program.join_program'
                                          ]
                                      )}
                            </Button>
                        </MaxColumnsContainer>
                    </Box>
                )}
            <GetStartedDialog
                {...getStartedDialogState}
                onClose={handleGetStartedClosed}
            />
        </Box>
    );
};
